'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import SignalWifiStatusbarConnectedNoInternet4Icon from '@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4';
import WifiTetheringErrorRoundedIcon from '@mui/icons-material/WifiTetheringErrorRounded';
import { Alert, Box } from '@mui/material';
import type { NetworkStatus } from '../../utilities/hooks/useNetworkStatus';
import { errorMessages } from '../api/network/errorMessages';
export type NetworkBannerProps = Pick<NetworkStatus, 'isOnline' | 'isConnectionUnstable' | 'isSlowConnection'>;
type BannerContent = {
  message: string;
  severity: 'error' | 'warning';
  Icon: typeof SignalWifiStatusbarConnectedNoInternet4Icon;
};
function resolveBannerContent({
  isOnline,
  isConnectionUnstable
}: Pick<NetworkBannerProps, 'isOnline' | 'isConnectionUnstable'>): BannerContent {
  if (isOnline) {
    if (isConnectionUnstable) {
      return {
        message: 'Connection unstable. Some actions may fail — we will retry automatically.',
        severity: 'warning',
        Icon: WifiTetheringErrorRoundedIcon
      };
    }
    return {
      message: 'Slow connection detected. Loading may take longer than usual.',
      severity: 'warning',
      Icon: WifiTetheringErrorRoundedIcon
    };
  }
  return {
    message: errorMessages.offline,
    severity: 'error',
    Icon: SignalWifiStatusbarConnectedNoInternet4Icon
  };
}

/**
 * Sticky global banner for offline or unstable connectivity states.
 * Deferred until after mount to avoid false positives during SSR/hydration.
 */
export function NetworkBanner(t0) {
  const $ = _c(13);
  const {
    isOnline,
    isConnectionUnstable,
    isSlowConnection
  } = t0;
  const [isMounted, setIsMounted] = React.useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsMounted(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  React.useEffect(t1, t2);
  if (!isMounted) {
    return null;
  }
  if (isOnline && !isConnectionUnstable && !isSlowConnection) {
    return null;
  }
  let t3;
  if ($[2] !== isConnectionUnstable || $[3] !== isOnline) {
    t3 = resolveBannerContent({
      isOnline,
      isConnectionUnstable
    });
    $[2] = isConnectionUnstable;
    $[3] = isOnline;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    message,
    severity,
    Icon
  } = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      flexShrink: 0,
      width: "100%"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== Icon) {
    t5 = <Icon fontSize="small" />;
    $[6] = Icon;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      borderRadius: 0,
      justifyContent: "center"
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== message || $[10] !== severity || $[11] !== t5) {
    t7 = <Box sx={t4}><Alert severity={severity} icon={t5} sx={t6}>{message}</Alert></Box>;
    $[9] = message;
    $[10] = severity;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}